import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Pencil } from 'lucide-react';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { useLocale } from '../../../../lib/locale-context';
import type { Message } from '../../../../lib/use-chat';
import {
  hasMeetingNotesSections,
  hasMeetingNotesShape,
  meetingLinkUrl,
  parseMeetingNotes,
  toggleActionItem,
  type MeetingNotesActionItem,
  type MeetingNotesCardData,
  type ParsedMeetingNotes,
} from '../../../../lib/meeting-notes-card';
import { meetingDuration, meetingTimeRange } from '../../../../lib/meeting-time';
import { useAttachmentContent } from '../../../../lib/use-attachment-content';
import { workspaceFileRefOf } from '../../../../lib/vfs-attachment';
import { parseFileRecordContent } from '../../../../lib/file-record';
import { fetchWorkspaces } from '../../../../lib/workspaces';
import { fetchWorkspaceFile, fetchWorkspaceFiles, type WorkspaceFile } from '../../../../lib/workspace-files';
import { queryClient, queryKeys } from '../../../../lib/query-client';
import { useFileWrite } from '../../../../lib/use-file-write';
import { useFileSystemDuplexer } from '../../../../lib/use-file-system-duplexer';
import { openFileForRow, useDocViewer } from '../../../../lib/doc-viewer-presenter';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useLoadBi } from '../../../../lib/use-load-bi';
import { useTasks } from '../../../../lib/tasks';
import { useActiveConversation } from '../../../chat/ActiveConversationContext';
import { useTaskSidePanel } from '../../task-side-panel/TaskSidePanelContext';
import { TaskDetailModal } from '../../tasks/TaskDetailModal';
import { Badge, IconButton, SelectionIndicator, StatusBadge, TextButton, Toast, Typography } from '../../ui';
import { AssistantMarkdownContent } from '../MessageRow/AssistantMarkdownContent';
import styles from './MeetingNotesCard.module.css';

/**
 * The meeting write-up, drawn in place of a bubble for the row carrying `metadata.meetingNotesCard`.
 * Mirrors iOS `MeetingNotesCard.swift` / Android `MeetingNotesCard.kt`; same surface as the brief's
 * `MeetingSummaryCard` (elevated, 20 padding, 16 radius, the reserved lime border).
 *
 * **The card is a rendering of a FILE.** The body is `notes.md` in the Meetings space, attached to this
 * row by path; the reader edits that file, ticks its `- [ ]` lines, and the card re-renders from it.
 * The row's own markdown is only how the file looked when the meeting ended, so it is the FALLBACK —
 * drawn when the file cannot be read, or when the parser finds nothing it recognises — and the row is
 * never rewritten.
 *
 * **The header facts come from the marker, never from the prose.** The meeting's own start, end and
 * duration (the transcript's first and last stamps, rendered in the reader's zone), the link, who was
 * in the room with the host marked, and who spoke with the agent marked. Names are display names people
 * chose in the room: text, never links, never resolved against contacts.
 *
 * Two actions on an item, deliberately independent (product rule P7 — nothing is created until the
 * owner says yes):
 * - a TICK rewrites that one line in the file, optimistically, re-reading the file first so two
 *   writers (a tick here, Maestro linking a task) never clobber each other from stale state;
 * - "Add as task" SENDS A MESSAGE into this same conversation. Maestro proposes, the owner confirms,
 *   and Maestro links the task back on the notes line; the card then shows the task's badge instead.
 */
export function MeetingNotesCard({ message, conversationId, canSend = true }: { message: Message; conversationId?: string; canSend?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { logPageClickBi } = useGenericBi();
  const card = message.meetingNotesCard as MeetingNotesCardData;

  // The file: the first markdown attachment carried by path, `notes.md` by name when there is one.
  const attachment = useMemo(() => notesAttachment(message.attachments), [message.attachments]);
  const { content, pending, error } = useAttachmentContent(attachment ?? NO_ATTACHMENT);
  const ref = attachment?.vfsPath ? workspaceFileRefOf(attachment.vfsPath) : null;
  const workspaces = useQuery({ queryKey: queryKeys.workspaces, queryFn: fetchWorkspaces, enabled: !!ref });
  const workspaceId = ref ? workspaces.data?.find((w) => w.slug === ref.slug)?.id : undefined;
  useLoadBi('meeting-notes-card', { isLoading: pending, isError: error });

  // The file changes under the card: Maestro appends a task link after "Add as task", another device
  // ticks a line, the editor saves. One shared duplexer connection carries every file-system change, so
  // subscribing here costs a callback, not a socket; both caches the card can be reading from are dropped.
  const vfsPath = attachment?.vfsPath;
  useFileSystemDuplexer(() => {
    if (vfsPath) void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceFile(vfsPath) });
    if (workspaceId) void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceFiles(workspaceId) });
  });

  // A stored file's raw content may open with a `<!--sym-file:…-->` FileRecord header. The card reads
  // and rewrites the BODY under it — line indexes are body-relative — and the write route re-attaches
  // the header from the existing file, exactly as the editor's Save does.
  const body = useMemo(() => parseFileRecordContent(content).body, [content]);
  const notes = useMemo(() => (body.trim() ? parseMeetingNotes(body) : null), [body]);
  // Two different questions. `shaped` is "can the card draw this file" — sections OR the template's
  // metadata bullets — and it decides the header facts and the body's form. `sections` is only whether
  // there is a BODY to draw: a meeting nobody spoke in has the bullets and no sections, and it is still
  // a card rather than a markdown dump.
  const shaped = notes && hasMeetingNotesShape(notes) ? notes : null;
  const sections = !!notes && hasMeetingNotesSections(notes);

  // The pencil: the same editor `/files` opens, on the same file. The viewer's own Save re-reads the
  // Space's files, which is what re-renders this card; the single-file fallback cache is refreshed when
  // the viewer closes, for the account where the file was only reachable that way.
  // Gated on the file being ADDRESSABLE and settled, not on it having content: a reader who cleared the
  // file must still get the pencil to refill it. Not while the read is pending, though — an editor opened
  // on a body that has not arrived would save an empty file over a full one.
  //
  // An EMPTY file and a FAILED read look identical through the content resolution (both settle to ''),
  // and they need opposite answers — so the file's own ROW in the Space's file list is what tells them
  // apart: present means the file exists and may be refilled, absent after a failed read means we do not
  // know what is in it and the editor must not offer to save over it. Same query key
  // `useAttachmentContent` already reads, so this costs no request.
  const files = useQuery({
    queryKey: queryKeys.workspaceFiles(workspaceId),
    queryFn: () => fetchWorkspaceFiles(workspaceId as string),
    enabled: !!workspaceId,
  });
  const fileRowExists = !!ref && !!files.data?.some((file) => !file.isApp && file.name === ref.name);
  const canEdit = !!ref && !!workspaceId && !pending && (!!content || fileRowExists);
  const viewer = useDocViewer();
  const openedViewer = useRef(false);
  useEffect(() => {
    if (viewer) return;
    if (!openedViewer.current) return;
    openedViewer.current = false;
    if (attachment?.vfsPath) void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceFile(attachment.vfsPath) });
  }, [viewer, attachment?.vfsPath]);
  const onEdit = () => {
    if (!ref || !workspaceId) return;
    logPageClickBi({ page_name: 'chat', button_name: 'meeting_notes_edit', conversation_id: conversationId });
    const file: WorkspaceFile = { name: ref.name, isApp: false, content, workspaceId, extension: 'md', contentType: 'text/markdown' };
    openedViewer.current = true;
    openFileForRow(file, 'document_viewer', undefined, undefined, { editing: true, directory: 'workspaces' });
  };

  const title = notes?.title || t('meetings.notes.title');

  return (
    <div className={styles.card} data-meeting-session={card.meetingSessionId}>
      <div className={styles.titleRow}>
        <Typography variant="h2" component="h3" color="strong" className={styles.title}>
          {title}
        </Typography>
        {canEdit && (
          <IconButton size="small" onClick={onEdit} aria-label={t('meetings.notes.edit')} title={t('meetings.notes.edit')}>
            <Pencil size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
          </IconButton>
        )}
      </div>
      {shaped?.lead && (
        <Typography variant="p2" color="secondary" component="p" className={styles.lead}>
          {shaped.lead}
        </Typography>
      )}
      {/* The parsed file, not the section gate: its date, link and attendee lines are what stand in for a
          marker field the row was written without, and a file with no sections has them too. */}
      <MeetingFacts card={card} notes={shaped} locale={locale} conversationId={conversationId} />
      {/* No rule under the facts when there is nothing below it — a file of bullets alone ends here. */}
      {(!shaped || sections || !!shaped.rest) && <div className={styles.divider} />}
      {shaped ? (
        <StructuredBody
          notes={shaped}
          content={body}
          vfsPath={attachment?.vfsPath}
          writeKey={ref && workspaceId ? `${workspaceId}/${ref.name}` : undefined}
          conversationId={conversationId}
          canSend={canSend}
        />
      ) : (
        // A file the parser cannot place at all (prose the template does not describe), or no readable
        // file: the write-up whole, as markdown.
        <div className={styles.body}>
          <AssistantMarkdownContent displayed={notes ? body : message.content || ''} isCursorOn={false} />
        </div>
      )}
    </div>
  );
}

const NO_ATTACHMENT: MessageAttachment = { fileName: '', mimeType: '', url: '' };

/** `notes.md` when attached by path; else the first markdown attachment carried by path. */
function notesAttachment(attachments: MessageAttachment[] | undefined): MessageAttachment | null {
  if (!attachments?.length) return null;
  const byPath = attachments.filter((a) => a.vfsPath && !a.url);
  return byPath.find((a) => /(^|\/)notes\.md$/i.test(a.fileName || a.vfsPath || '')) ?? byPath.find((a) => a.mimeType === 'text/markdown' || /\.md$/i.test(a.fileName)) ?? null;
}

/**
 * The header facts. From the marker first; the file's own metadata bullets stand in for anything the
 * marker lacks (a row written before a field existed), so an older meeting still reads whole.
 */
function MeetingFacts({ card, notes, locale, conversationId }: { card: MeetingNotesCardData; notes: ParsedMeetingNotes | null; locale: string; conversationId?: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const range = meetingTimeRange(card.startedAt, card.endedAt, Date.now(), locale, {
    today: t('agents.activity.today'),
    yesterday: t('agents.activity.yesterday'),
  });
  const duration = meetingDuration(card.startedAt, card.endedAt);
  const durationLabel = duration
    ? duration.hours > 0
      ? t('meetings.duration.hoursMinutes', { hours: duration.hours, minutes: duration.minutes })
      : t('meetings.duration.minutes', { count: duration.minutes })
    : '';
  const when = [range, durationLabel].filter(Boolean).join(' · ') || notes?.meta.date || '';
  // The link is metadata the calendar and the room supplied, rendered as a raw `<a href>`: only a web
  // URL becomes a link, and anything else (a `javascript:` or `data:` scheme) is dropped, not shown.
  const url = meetingLinkUrl(card.meetingUrl) ?? meetingLinkUrl(notes?.meta.meetingUrl);
  const attending = card.participants.length > 0 ? card.participants.map((p) => (p.isHost ? `${p.name} (${t('meetings.notes.host')})` : p.name)).join(', ') : notes?.meta.attending;
  const spoke = card.speakers.length > 0 ? card.speakers.map((s) => (s.isAgent ? `${s.name} (${t('meetings.notes.aiAgent')})` : s.name)).join(', ') : notes?.meta.spoke;

  const rows: { key: string; label: string; value: ReactNode }[] = [];
  if (when) rows.push({ key: 'date', label: t('meetings.notes.date'), value: <span className={styles.tabular}>{when}</span> });
  if (url) {
    rows.push({
      key: 'link',
      label: t('meetings.notes.link'),
      value: (
        <TextButton
          size="small"
          href={url}
          external
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<ExternalLink size={12} aria-hidden />}
          onClick={() => logPageClickBi({ page_name: 'chat', button_name: 'meeting_notes_open_link', conversation_id: conversationId })}
        >
          {displayHost(url)}
        </TextButton>
      ),
    });
  }
  // Names are TEXT. Never a link, never a mention, never a contact lookup — they are what people typed
  // into the room, and two people can type the same one.
  if (attending) rows.push({ key: 'attending', label: t('meetings.notes.attending'), value: attending });
  if (spoke) rows.push({ key: 'spoke', label: t('meetings.notes.spoke'), value: spoke });
  if (rows.length === 0) return null;

  return (
    <dl className={styles.facts}>
      {rows.map((row) => (
        <div key={row.key} className={styles.fact}>
          <dt className={styles.factLabel}>{row.label}</dt>
          <dd className={styles.factValue} dir="auto">
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** "wix.zoom.us/j/897…" — the link as a reader recognises it, not a 90-character URL. */
function displayHost(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname;
    const shown = `${u.host}${path}`;
    return shown.length > 40 ? `${shown.slice(0, 39)}…` : shown;
  } catch {
    return url.length > 40 ? `${url.slice(0, 39)}…` : url;
  }
}

/**
 * TLDR, Updates, Action items, Open questions, then anything the parser could not place as markdown.
 * Owns the tick and the add-as-task actions, because both need the file's write key.
 */
function StructuredBody({
  notes,
  content,
  vfsPath,
  writeKey,
  conversationId,
  canSend,
}: {
  notes: ParsedMeetingNotes;
  content: string;
  vfsPath: string | undefined;
  writeKey: string | undefined;
  conversationId?: string;
  /** A send can happen now. False while a turn streams: the actions render, disabled. */
  canSend: boolean;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { write } = useFileWrite();
  const { openChatWithMessage } = useActiveConversation();
  const [toast, setToast] = useState<string | null>(null);

  // Optimistic ticks, keyed by line. Cleared whenever the FILE changes under us — the file is the truth
  // and a stale overlay on fresh content would show a tick the file does not have.
  const [optimistic, setOptimistic] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState<Set<number>>(() => new Set());
  // Writes to ONE file go through one queue. Two ticks in quick succession each re-read the file and
  // write the whole of it; run in parallel, the second read happens before the first write lands and the
  // second write erases the first tick. Queued, the second job's read sees the first job's write.
  const writeQueue = useRef<Promise<void>>(Promise.resolve());
  // When the file changes under us, drop only the optimistic entries the file now agrees with; an entry
  // whose write is still in the queue stays, so a refetch mid-queue does not flip a ticked row back.
  useEffect(() => {
    setOptimistic((prev) => {
      const next: Record<number, boolean> = {};
      for (const item of notes.actionItems) {
        const wanted = prev[item.lineIndex];
        if (wanted !== undefined && wanted !== item.done) next[item.lineIndex] = wanted;
      }
      return next;
    });
  }, [content, notes.actionItems]);

  const doneOf = (item: MeetingNotesActionItem) => optimistic[item.lineIndex] ?? item.done;
  const canTick = !!writeKey && !!vfsPath;
  const canAsk = !!conversationId;

  const onTick = (item: MeetingNotesActionItem) => {
    if (!canTick || saving.has(item.lineIndex)) return;
    const wasDone = doneOf(item);
    logPageClickBi({ page_name: 'chat', button_name: wasDone ? 'meeting_notes_untick' : 'meeting_notes_tick', conversation_id: conversationId });
    setOptimistic((prev) => ({ ...prev, [item.lineIndex]: !wasDone }));
    setSaving((prev) => new Set(prev).add(item.lineIndex));
    writeQueue.current = writeQueue.current.then(() => writeTick(item, wasDone)).catch(() => undefined);
  };

  const writeTick = async (item: MeetingNotesActionItem, wasDone: boolean) => {
    const settle = () => {
      setOptimistic((prev) => {
        const next = { ...prev };
        delete next[item.lineIndex];
        return next;
      });
    };
    const revert = (reason: string) => {
      settle();
      setToast(reason);
    };
    try {
      // Re-read before rewriting: Maestro may have appended a task link, or another device ticked a
      // line, since this card loaded. The line is found again in the FRESH file — by index when the
      // text still matches, by text when the file moved.
      //
      // A failed or empty read REVERTS; it never falls back to the body this card loaded earlier. That
      // body is exactly the stale state the plan's risk table rules out: a whole-file write computed
      // from it would silently undo a task link Maestro wrote in between, in the file the user treats
      // as the record of the meeting. A tick that does not stick, with a toast, is the cheaper failure.
      // Same rule on both phones.
      let fresh = '';
      try {
        fresh = parseFileRecordContent(await fetchWorkspaceFile(vfsPath as string)).body;
      } catch {
        fresh = '';
      }
      if (!fresh.trim()) {
        revert(t('meetings.notes.tickFailed'));
        return;
      }
      const freshItems = parseMeetingNotes(fresh).actionItems;
      const target = freshItems.find((i) => i.lineIndex === item.lineIndex && i.text === item.text) ?? freshItems.find((i) => i.text === item.text);
      if (!target) {
        revert(t('meetings.notes.tickFailed'));
        void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceFile(vfsPath) });
        return;
      }
      // Already in the state the reader asked for (ticked elsewhere): nothing to write, just catch up.
      const next = target.done === !wasDone ? fresh : toggleActionItem(fresh, target.lineIndex);
      const ok = next === fresh ? true : await write(writeKey as string, next, { directory: 'workspaces' });
      if (!ok) {
        revert(t('meetings.notes.tickFailed'));
        return;
      }
      // Both caches the card reads from now hold the file as written, so the re-render is immediate
      // and the optimistic overlay can go.
      queryClient.setQueryData(queryKeys.workspaceFile(vfsPath), next);
      settle();
    } catch {
      revert(t('meetings.notes.tickFailed'));
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(item.lineIndex);
        return next;
      });
    }
  };

  const describe = (item: MeetingNotesActionItem): string => {
    const details = [item.owner ? t('meetings.notes.itemOwner', { owner: item.owner }) : '', item.due ? t('meetings.notes.itemDue', { due: item.due }) : ''].filter(Boolean);
    return details.length ? `${item.text} (${details.join(', ')})` : item.text;
  };
  const openItems = notes.actionItems.filter((item) => !doneOf(item) && !item.taskId);
  const onAddTask = (item: MeetingNotesActionItem) => {
    if (!conversationId) return;
    logPageClickBi({ page_name: 'chat', button_name: 'meeting_notes_add_task', conversation_id: conversationId });
    openChatWithMessage({ kind: 'existing', conversationId, message: t('meetings.notes.addTaskMessage', { item: describe(item) }) });
  };
  const onAddAll = () => {
    if (!conversationId || openItems.length === 0) return;
    logPageClickBi({ page_name: 'chat', button_name: 'meeting_notes_add_all_tasks', conversation_id: conversationId, answer: String(openItems.length) });
    const list = openItems.map((item) => `- ${describe(item)}`).join('\n');
    openChatWithMessage({ kind: 'existing', conversationId, message: `${t('meetings.notes.addAllTasksMessage')}\n${list}` });
  };

  return (
    <div className={styles.body}>
      {notes.tldr && (
        <Section title={t('meetings.notes.tldr')}>
          <Typography variant="p2" color="primary" component="p" className={styles.paragraph}>
            {notes.tldr}
          </Typography>
        </Section>
      )}
      {notes.updates.length > 0 && (
        <Section title={t('meetings.notes.updates')}>
          <ul className={styles.list}>
            {notes.updates.map((update, i) => (
              <li key={i} className={styles.listItem} dir="auto">
                <Typography variant="p2" color="primary" component="span">
                  <strong className={styles.who}>{update.who}:</strong> {update.text}
                </Typography>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {notes.actionItems.length > 0 && (
        <Section
          title={t('meetings.notes.actionItems')}
          action={
            canAsk && openItems.length > 1 ? (
              <TextButton size="small" onClick={onAddAll} disabled={!canSend}>
                {t('meetings.notes.addAllAsTasks')}
              </TextButton>
            ) : undefined
          }
        >
          <ul className={styles.list}>
            {notes.actionItems.map((item) => (
              <ActionItemRow
                key={item.lineIndex}
                item={item}
                done={doneOf(item)}
                busy={saving.has(item.lineIndex)}
                onTick={canTick ? () => onTick(item) : undefined}
                onAddTask={canAsk ? () => onAddTask(item) : undefined}
                canSend={canSend}
                conversationId={conversationId}
              />
            ))}
          </ul>
          {canAsk && openItems.length > 0 && (
            <Typography variant="p3" color="secondary" component="p" className={styles.footnote}>
              {t('meetings.notes.notCreatedUntilYes')}
            </Typography>
          )}
        </Section>
      )}
      {notes.openQuestions.length > 0 && (
        <Section title={t('meetings.notes.openQuestions')}>
          <ul className={styles.list}>
            {notes.openQuestions.map((question, i) => (
              <li key={i} className={styles.listItem} dir="auto">
                <Typography variant="p2" color="primary" component="span">
                  {question}
                </Typography>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {notes.rest && (
        <div className={styles.rest}>
          <AssistantMarkdownContent displayed={notes.rest} isCursorOn={false} />
        </div>
      )}
      <Toast open={toast !== null} message={toast ?? ''} severity="error" onClose={() => setToast(null)} />
    </div>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <Typography variant="h4" color="strong" component="h4" className={styles.sectionTitle}>
          {title}
        </Typography>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * One `- [ ]` line: the DS checkbox mark over a real checkbox input, the text, the owner · due meta
 * line, and on the trailing edge either the task's badge (linked) or "Add as task" (open).
 */
function ActionItemRow({
  item,
  done,
  busy,
  onTick,
  onAddTask,
  canSend,
  conversationId,
}: {
  item: MeetingNotesActionItem;
  done: boolean;
  busy: boolean;
  onTick?: () => void;
  onAddTask?: () => void;
  canSend: boolean;
  conversationId?: string;
}) {
  const { t } = useTranslation();
  const meta = [item.owner, item.due].filter(Boolean).join(' · ');
  const disabled = !onTick || busy;
  return (
    <li className={styles.actionRow}>
      <label className={`${styles.check} ${disabled ? styles.checkDisabled : ''}`}>
        {/* Literal `type` so the input guard can see this is a checkbox, not a text field. */}
        <input type="checkbox" className={styles.checkInput} checked={done} disabled={disabled} onChange={() => onTick?.()} aria-label={item.text} />
        <SelectionIndicator checked={done} shape="checkbox" disabled={disabled} />
        <span className={styles.actionText} dir="auto">
          <Typography variant="p2" color="primary" component="span" className={done ? styles.done : undefined}>
            {item.text}
          </Typography>
          {meta && (
            <Typography variant="p3" color="secondary" component="span" className={styles.actionMeta}>
              {meta}
            </Typography>
          )}
        </span>
      </label>
      <span className={styles.actionTrailing}>
        {item.taskId ? (
          <LinkedTaskBadge taskId={item.taskId} conversationId={conversationId} />
        ) : (
          !done &&
          onAddTask && (
            <TextButton size="small" onClick={onAddTask} disabled={!canSend}>
              {t('meetings.notes.addAsTask')}
            </TextButton>
          )
        )}
      </span>
    </li>
  );
}

/** The linked task's status badge, opening the task. A task the list has not loaded yet wears a plain
 *  "Task" badge — the link is real even before its status is known. */
function LinkedTaskBadge({ taskId, conversationId }: { taskId: string; conversationId?: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { openModal } = useTaskSidePanel();
  const { state } = useTasks();
  const task = state.kind === 'ready' ? state.tasks.find((candidate) => candidate.id === taskId) : undefined;
  const open = () => {
    logPageClickBi({ page_name: 'chat', button_name: 'meeting_notes_open_task', task_id: taskId, conversation_id: conversationId });
    openModal(<TaskDetailModal taskId={taskId} />, taskId);
  };
  return (
    <TextButton size="small" onClick={open} aria-label={t('meetings.notes.openTask')} className={styles.taskButton}>
      {task ? <StatusBadge status={task.status} /> : <Badge variant="in-progress" label={t('meetings.notes.taskLinked')} />}
    </TextButton>
  );
}
